import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useUserStore } from '../stores/userStore';
import { authApi } from '../api/auth';
import { meApi } from '../api/me';
import { adminApi } from '../api/admin';
import { objectStorageApi } from '../api/objectStorage';
import { usersApi } from '../api/users';
import { saveCurrentPathAndRedirectToLogin } from '../utils/authUtils';
import { useError } from '../contexts/ErrorContext';
import imageCompression from 'browser-image-compression';

export const BIO_MAX_LENGTH = 300;
export const NICKNAME_MAX_LENGTH = 14;

export const useUserProfile = (nickname) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showError } = useError();
  const {
    user: loggedInUser,
    isAdmin,
    setUser: setUserInStore,
    clearUser,
    loading: userLoading,
  } = useUserStore();

  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [profileForm, setProfileForm] = useState({ nickname: '', bio: '' });
  const [isSuspensionDialogOpen, setIsSuspensionDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isMyImageDeleteDialogOpen, setIsMyImageDeleteDialogOpen] = useState(false);
  const [isReactivationDialogOpen, setIsReactivationDialogOpen] = useState(false);
  const [imageMenuAnchor, setImageMenuAnchor] = useState(null);
  const [userError, setUserError] = useState(null);

  const viewMode = useMemo(() => {
    if (!nickname || (loggedInUser && loggedInUser.nickname === nickname)) {
      return 'myPage';
    }
    if (isAdmin) {
      return 'admin';
    }
    return 'public';
  }, [nickname, loggedInUser, isAdmin]);

  // 마이페이지가 아닌 경우에만 별도 사용자 정보 로드
  const [otherUser, setOtherUser] = useState(null);
  const [myProfile, setMyProfile] = useState(null);

  useEffect(() => {
    // userLoading이 true면 아무것도 하지 않음
    if (userLoading) {
      return;
    }

    const fetchUserData = async () => {
      setLoading(true);
      try {
        const userData = (await usersApi.getUser(nickname)).data;
        let profileData = userData;
        const token = localStorage.getItem('POSTPOP_AUTH_TOKEN');
        if (token && loggedInUser && loggedInUser.id !== userData.id) {
          try {
            profileData = (await usersApi.getFollowStatus(userData.id)).data;
          } catch (_) {
            profileData = userData;
          }
        }
        setOtherUser(profileData);
        setProfileForm({ nickname: profileData.nickname, bio: profileData.bio || '' });
      } catch (err) {
        setOtherUser(null);
        const status = err.response?.status;
        if (status === 403) {
          setUserError('탈퇴한 회원입니다.');
        } else if (status === 404) {
          setUserError('존재하지 않는 회원입니다.');
        }
      } finally {
        setLoading(false);
      }
    };

    // userLoading이 false일 때 토큰 존재 여부를 확인
    if (viewMode === 'myPage') {
      const token = localStorage.getItem('POSTPOP_AUTH_TOKEN');
      if (!token) {
        // 토큰이 없으면 로그인 페이지로 리다이렉트
        saveCurrentPathAndRedirectToLogin(navigate);
        return;
      }
      if (!loggedInUser) {
        // 토큰은 있지만 loggedInUser가 null이면 아직 로딩 중이거나 토큰이 만료된 상태
        // 잠시 기다린 후 다시 확인
        return;
      }
      const fetchMyProfile = async () => {
        setLoading(true);
        try {
          const userData = (await usersApi.getUser(loggedInUser.nickname)).data;
          setMyProfile({ ...loggedInUser, ...userData });
          if (!isEditing) {
            setProfileForm({ nickname: userData.nickname, bio: userData.bio || '' });
          }
        } catch (_) {
          setMyProfile(loggedInUser);
          if (!isEditing) {
            setProfileForm({ nickname: loggedInUser.nickname, bio: loggedInUser.bio || '' });
          }
        } finally {
          setLoading(false);
        }
      };
      fetchMyProfile();
    } else {
      fetchUserData();
    }
  }, [nickname, viewMode, loggedInUser, navigate, userLoading, isEditing]);

  // 마이페이지는 전역 state 사용, 다른 경우는 로컬 state 사용
  const user = viewMode === 'myPage' ? myProfile || loggedInUser : otherUser;

  const setProfileUser = useCallback(
    (updater) => {
      if (viewMode === 'myPage') {
        setMyProfile(updater);
      } else {
        setOtherUser(updater);
      }
    },
    [viewMode],
  );

  const handleProfileInputChange = useCallback(
    (e) => {
      if (viewMode !== 'myPage' && viewMode !== 'admin') return;
      const { name, value } = e.target;

      if (name === 'bio' && value.length > BIO_MAX_LENGTH) {
        return;
      }

      if (name === 'nickname' && value.length > NICKNAME_MAX_LENGTH) {
        return;
      }

      setProfileForm((prev) => ({ ...prev, [name]: value }));
    },
    [viewMode],
  );

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    if (viewMode !== 'myPage' && viewMode !== 'admin') return;

    // 닉네임 검증
    const newNickname = profileForm.nickname;
    if (!newNickname || newNickname.length < 2 || newNickname.length > NICKNAME_MAX_LENGTH) {
      showError(`닉네임은 한글, 영문, 숫자 (2~${NICKNAME_MAX_LENGTH}자)로 입력해야 합니다.`);
      return;
    }

    // 한글, 영문, 숫자만 허용하는 정규식
    const nicknameRegex = /^[가-힣a-zA-Z0-9\s]+$/;
    if (!nicknameRegex.test(newNickname)) {
      showError(`닉네임은 한글, 영문, 숫자, 공백 (2~${NICKNAME_MAX_LENGTH}자)로 입력해야 합니다.`);
      return;
    }

    try {
      if (viewMode === 'myPage') {
        await meApi.updateProfile(profileForm);
        if (nickname && newNickname !== nickname) {
          window.location.replace(`/users/${encodeURIComponent(newNickname)}`);
          return;
        }
        const updatedUser = (await authApi.getMe()).data;
        setUserInStore(updatedUser);
        try {
          const updatedProfile = (await usersApi.getUser(updatedUser.nickname)).data;
          setMyProfile({ ...updatedUser, ...updatedProfile });
        } catch (_) {
          setMyProfile(updatedUser);
        }
      } else {
        // admin: bio가 변경되지 않았으면 null로 보내서 업데이트 제외
        const originalBio = otherUser?.bio || '';
        const newBio = originalBio === profileForm.bio ? null : profileForm.bio;
        await adminApi.updateUserProfile(otherUser.id, { ...profileForm, bio: newBio });
        setOtherUser((prev) => ({ ...prev, nickname: profileForm.nickname, bio: profileForm.bio }));
      }
      setIsEditing(false);
    } catch (err) {}
  };

  const handleCancelEdit = useCallback(() => {
    setIsEditing(false);
    if (user) {
      setProfileForm({ nickname: user.nickname, bio: user.bio || '' });
    }
  }, [user]);

  // MyPage only actions
  const handleLogout = () => {
    if (viewMode !== 'myPage') return;
    try {
      localStorage.removeItem('POSTPOP_AUTH_TOKEN');
      clearUser();
      queryClient.clear();
      navigate('/login');
    } catch (err) {}
  };

  const updateProfileImage = async (imageUrl) => {
    try {
      await meApi.updateProfile({ profileImageUrl: imageUrl, isProfileImageChanged: true });
      const updatedUser = (await authApi.getMe()).data;
      setUserInStore(updatedUser);
      setMyProfile((prev) => ({ ...(prev || updatedUser), ...updatedUser }));
    } catch (err) {}
  };

  const handleImageUpload = async (file) => {
    if (!file || viewMode !== 'myPage') return;

    // 파일 크기 검증 (20MB = 20 * 1024 * 1024 bytes)
    const maxSize = 20 * 1024 * 1024;
    if (file.size > maxSize) {
      showError('최대 20MB까지 업로드할 수 있습니다.');
      return;
    }

    // 파일 타입 검증
    if (!file.type.startsWith('image/')) {
      showError('이미지 파일만 업로드 가능합니다.');
      return;
    }

    try {
      const options = {
        maxSizeMB: 2,
        maxWidthOrHeight: 400,
        useWebWorker: true,
      };
      const compressedFile = await imageCompression(file, options);
      const formData = new FormData();
      formData.append('image', compressedFile);
      const response = await objectStorageApi.uploadImage(formData);
      await updateProfileImage(response.data.url);
    } catch (err) {
    } finally {
      setImageMenuAnchor(null);
    }
  };

  const handleMyImageDelete = () => {
    if (viewMode !== 'myPage') return;
    setIsMyImageDeleteDialogOpen(true);
    setImageMenuAnchor(null);
  };

  const handleConfirmMyImageDelete = async () => {
    if (viewMode !== 'myPage') return;
    try {
      await updateProfileImage(null);
    } catch (err) {
    } finally {
      setIsMyImageDeleteDialogOpen(false);
    }
  };

  // Admin only actions
  const handleSuspend = () => {
    if (viewMode === 'admin') {
      setIsSuspensionDialogOpen(true);
    }
  };

  const handleConfirmSuspend = async (reason) => {
    if (viewMode !== 'admin') return;
    try {
      await adminApi.updateUsersStatus({
        userIds: [otherUser.id],
        isActive: false,
        reason: reason,
      });
      setIsSuspensionDialogOpen(false);
      setOtherUser((prevUser) => ({ ...prevUser, isActive: false }));
    } catch (err) {}
  };

  const handleUnsuspend = () => {
    if (viewMode !== 'admin') return;
    setIsReactivationDialogOpen(true);
  };

  const handleConfirmUnsuspend = async () => {
    if (viewMode !== 'admin') return;
    try {
      await adminApi.updateUsersStatus({
        userIds: [otherUser.id],
        isActive: true,
      });
      setOtherUser((prevUser) => ({ ...prevUser, isActive: true }));
    } catch (err) {}
    setIsReactivationDialogOpen(false);
  };

  const handleDeleteProfileImage = () => {
    if (viewMode === 'admin') {
      setIsDeleteDialogOpen(true);
    }
  };

  const handleConfirmDelete = async () => {
    if (viewMode !== 'admin') return;
    try {
      await adminApi.updateUserProfile(otherUser.id, {
        nickname: profileForm.nickname,
        profileImageUrl: null,
        isProfileImageChanged: true,
        bio: null,
      });
      setOtherUser((prevUser) => ({ ...prevUser, profileImageUrl: null }));
    } catch (err) {}
    setIsDeleteDialogOpen(false);
  };

  const handleConfirmGradeChange = async (grade) => {
    if (viewMode !== 'admin') return;
    try {
      await adminApi.updateUsersGrade([otherUser.id], grade);
      setOtherUser((prevUser) => ({ ...prevUser, grade }));
      return true;
    } catch (err) {
      return false;
    }
  };

  return {
    user,
    setProfileUser,
    loading,
    viewMode,
    isEditing,
    setIsEditing,
    profileForm,
    isSuspensionDialogOpen,
    isDeleteDialogOpen,
    setIsSuspensionDialogOpen,
    setIsDeleteDialogOpen,
    isReactivationDialogOpen,
    setIsReactivationDialogOpen,
    isMyImageDeleteDialogOpen,
    setIsMyImageDeleteDialogOpen,
    imageMenuAnchor,
    setImageMenuAnchor,
    handleProfileInputChange,
    handleProfileSubmit,
    handleCancelEdit,
    handleLogout,
    handleImageUpload,
    handleMyImageDelete,
    handleConfirmMyImageDelete,
    handleSuspend,
    handleConfirmSuspend,
    handleUnsuspend,
    handleConfirmUnsuspend,
    handleDeleteProfileImage,
    handleConfirmDelete,
    handleConfirmGradeChange,
    userError,
    setUserError,
  };
};
